import { Controller } from 'stimulus'
import { materializeLazyCell } from '@helpers'

export default class extends Controller {
  static targets = ['field', 'form']

  connect() {
    this.setOriginalValue()
    this.isKeyboardNav = false
    this.isNavigating = false
    this.boundHandleAjaxError = this.handleAjaxError.bind(this)
    this.boundHandleAjaxSuccess = this.handleAjaxSuccess.bind(this)
    this.boundHandleFieldInput = this.handleFieldInput.bind(this)
    this.boundHandleFieldFocus = this.handleFieldFocus.bind(this)
    this.boundFlushOnUnload = this.flushOnUnload.bind(this)
    this.formTarget.addEventListener('ajax:error', this.boundHandleAjaxError)
    this.formTarget.addEventListener('ajax:success', this.boundHandleAjaxSuccess)
    this.fieldTarget.addEventListener('input', this.boundHandleFieldInput)
    this.fieldTarget.addEventListener('focus', this.boundHandleFieldFocus)
    window.addEventListener('pagehide', this.boundFlushOnUnload)

    // A lazy cell materialized and focused via keyboard navigation fired its
    // focus event before this controller connected — register the
    // outside-click-to-close listener it missed. No-op on normal page renders
    // (the field is hidden, so it can't be the active element).
    if (document.activeElement === this.fieldTarget) {
      this.handleFieldFocus()
    }
  }

  disconnect() {
    if (this.boundHandleAjaxError) {
      this.formTarget.removeEventListener('ajax:error', this.boundHandleAjaxError)
    }
    if (this.boundHandleAjaxSuccess) {
      this.formTarget.removeEventListener('ajax:success', this.boundHandleAjaxSuccess)
    }
    if (this.boundHandleFieldInput) {
      this.fieldTarget.removeEventListener('input', this.boundHandleFieldInput)
    }
    if (this.boundHandleFieldFocus) {
      this.fieldTarget.removeEventListener('focus', this.boundHandleFieldFocus)
    }
    if (this.boundFlushOnUnload) {
      window.removeEventListener('pagehide', this.boundFlushOnUnload)
    }
    if (this.handleOutsideClick) {
      document.removeEventListener('mousedown', this.handleOutsideClick)
      this.handleOutsideClick = null
    }
  }

  // If the page is being unloaded (tab close, browser quit, laptop sleep) while
  // the cell has an unsaved typed value, the input never blurs and submitIfUpdated
  // never fires — so the value would be lost. Best-effort flush via sendBeacon,
  // which is the only request type that survives unload in modern browsers.
  flushOnUnload() {
    const field = $(this.fieldTarget)
    const currentValue = field.val()
    const originalValue = field.data('originalValue')
    if (currentValue === originalValue) return
    if (typeof navigator.sendBeacon !== 'function') return

    const form = this.formTarget
    if (!form || !form.action) return

    let queued = false
    try {
      queued = navigator.sendBeacon(form.action, new FormData(form))
    } catch (_) {
      // Best effort; no recovery path during unload.
    }
    // sendBeacon returning false means the browser refused to queue (page state,
    // size limit, etc.) — that's a silent loss case worth surfacing in Airbrake.
    this.logDiagnosticBeacon('inline_edit_unload_beacon', {
      beacon_queued: queued,
      notify_airbrake: !queued
    })
  }

  diagnosticContext() {
    const field = this.fieldTarget
    const container = field?.closest('.inline-editable-form-container')
    const $field = $(field)
    return {
      cell_id: container?.id || null,
      item_id: container?.dataset?.itemId || null,
      field_tag: field?.tagName?.toLowerCase() || null,
      field_type: field?.type || null,
      current_value_length: ($field.val() ?? '').length,
      original_value_length: ($field.data('originalValue') ?? '').length,
      needs_submit: $(container).data('needsSubmit') === true,
      keyboard_nav: $(container).data('keyboardNav') === true,
      form_connected: !!this.formTarget?.isConnected,
      pending_submit: this.pendingSubmitValue !== undefined,
      no_fetch_value: this.element.dataset.noFetchValue === 'true',
      path: window.location.pathname,
      ua: navigator.userAgent.substring(0, 200)
    }
  }

  // Fire-and-forget POST to /client_diagnostics. keepalive: true lets the
  // request survive a short delay (not full unload — see logDiagnosticBeacon).
  logDiagnostic(event, extra = {}) {
    try {
      const { notify_airbrake, ...rest } = extra
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      fetch('/client_diagnostics', {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': token || ''
        },
        body: JSON.stringify({
          event,
          notify_airbrake: !!notify_airbrake,
          context: { ...this.diagnosticContext(), ...rest }
        })
      }).catch(() => {})
    } catch (_) {
      // best-effort — never let diagnostics throw into the calling path
    }
  }

  // sendBeacon variant for use during pagehide/unload, when fetch (even with
  // keepalive) is not reliable. Cannot set custom headers, so the
  // /client_diagnostics endpoint skips CSRF verification.
  logDiagnosticBeacon(event, extra = {}) {
    try {
      const { notify_airbrake, ...rest } = extra
      const payload = JSON.stringify({
        event,
        notify_airbrake: !!notify_airbrake,
        context: { ...this.diagnosticContext(), ...rest }
      })
      const blob = new Blob([payload], { type: 'application/json' })
      navigator.sendBeacon('/client_diagnostics', blob)
    } catch (_) {}
  }

  cellContainer() {
    return $(this.fieldTarget).closest('.inline-editable-form-container')
  }

  markPending() {
    this.cellContainer().addClass('pending-save').removeClass('saving')
  }

  // Cache the TD node when we start saving. After the PATCH returns, jobs#update.js.erb
  // replaces the TD's innerHTML, which detaches the form/field. At that point cellContainer()
  // walks up from the detached field and returns nothing — so markClean would fail to remove
  // the class from the real (still-attached) TD and the spinner would spin forever.
  markSaving() {
    this.savingContainerEl = this.cellContainer().get(0)
    $(this.savingContainerEl).removeClass('pending-save').addClass('saving')
  }

  markClean() {
    if (this.savingContainerEl) {
      $(this.savingContainerEl).removeClass('pending-save saving')
      this.savingContainerEl = null
    } else {
      this.cellContainer().removeClass('pending-save saving')
    }
  }

  handleFieldInput() {
    const field = $(this.fieldTarget)
    const currentValue = field.val()
    const originalValue = field.data('originalValue')
    if (currentValue !== originalValue) {
      this.markPending()
    } else {
      this.markClean()
    }
  }

  // Register the outside-click listener whenever the input gains focus, regardless
  // of how it got focus (mouse click via showForm, Tab/Enter via navigateToCell,
  // arrow keys, programmatic). Without this, keyboard-navigated cells have no
  // listener — clicking on a non-focusable element won't blur the input and the
  // form stays open with the typed value visible.
  handleFieldFocus() {
    this.currentContainer = this.cellContainer().get(0)
    this.startOutsideClickListener()
  }

  handleAjaxSuccess() {
    this.pendingSubmitValue = undefined
    this.markClean()
  }

  handleAjaxError(event) {
    const [, status, xhr] = event.detail || []
    const field = $(this.fieldTarget)
    const originalValue = field.data('originalValue') ?? ''
    const container = field.closest('.inline-editable-form-container')

    field.val(originalValue)
    container.find('.value').show()
    container.find('.form').hide()
    this.pendingSubmitValue = undefined
    this.markClean()

    let message = 'Could not save — your change was reverted.'
    try {
      const body = xhr && xhr.responseText ? JSON.parse(xhr.responseText) : null
      if (body && Array.isArray(body.errors) && body.errors.length) {
        message = `Could not save: ${body.errors.join(', ')}`
      }
    } catch (_) {}

    if (window.toastr) window.toastr.error(message)
  }

  setOriginalValue() {
    const field = $(this.fieldTarget)
    field.show()
    requestAnimationFrame(() => {
      field.focus()
      field.select()
      // rAF defers this past the current frame, so if the user clicked into the
      // cell in the meantime, showForm/navigateToCell may have seeded
      // originalValue already. Don't overwrite — see showForm() for why.
      if (field.data('originalValue') === undefined) {
        field.data('originalValue', field.val())
      }

      const inputType = field.attr('type')
      const skipPicker = field.is('[data-skip-picker]')

      if (
        !skipPicker
        && !field.data('justShown')
        && (inputType === 'date' || inputType === 'datetime-local')
      ) {
        if (typeof field[0]?.showPicker === 'function') {
          field[0].showPicker()
          field.data('justShown', true)
        }
      }
    })
  }

  submitIfUpdated(event) {
    const field = $(this.fieldTarget)
    const currentValue = field.val()
    const originalValue = field.data('originalValue')
    const dirty = currentValue !== originalValue

    // If focus is moving to another element inside this same inline editor
    // (e.g., value input -> unit select in the trait number cell, or the
    // comments/SRFV expand link — its text_expand_edit.js.erb carries the
    // dirty value into the modal), don't submit.
    if (event.relatedTarget && this.element.contains(event.relatedTarget)) {
      if (dirty) this.logDiagnostic('inline_edit_blur_skipped_internal_focus', {})
      return
    }

    const container = field.closest('.inline-editable-form-container')
    const partial = container.data('partial')
    const noFetch = this.element.dataset.noFetchValue === 'true'

    // For dropdowns in keyboard navigation mode, skip instant save on input event
    const tagName = field.prop('tagName')?.toLowerCase()
    const isInputEvent = event.type === 'input'
    const isKeyboardNav = container.data('keyboardNav') === true

    if (tagName === 'select' && isKeyboardNav && isInputEvent) {
      // Don't submit on input event when navigating with keyboard
      // Will submit on blur or when navigating away
      return
    }

    // Check if this cell needs to submit (marked during keyboard navigation)
    const needsSubmit = container.data('needsSubmit') === true

    if (needsSubmit || dirty) {
      // Clear the needsSubmit flag
      container.removeData('needsSubmit')
      // Selects save on their input event, and originalValue stays stale until
      // the server response re-renders the cell — so the blur that follows
      // would fire a duplicate PATCH. Skip only when the identical value is
      // already in flight; a further edit still submits.
      if (currentValue === this.pendingSubmitValue) return
      // If the form has been detached (broadcast race), Rails.fire silently no-ops
      // and the typed value is lost. Surface that before attempting submit.
      if (!this.formTarget.isConnected) {
        this.logDiagnostic('inline_edit_submit_detached_form', { notify_airbrake: true })
      }
      this.submitForm(event)
    } else if (!noFetch && partial) {
      this.fetchPartial(container, partial)
    } else {
      this.hideForm(container)
    }
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      this.triggerEscapeAction()
      return
    }

    // Alt+Enter inserts a line break in textareas
    if (event.key === 'Enter' && event.altKey && $(this.fieldTarget).prop('tagName')?.toLowerCase() === 'textarea') {
      event.preventDefault()
      const textarea = this.fieldTarget
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      textarea.value = textarea.value.substring(0, start) + '\n' + textarea.value.substring(end)
      textarea.selectionStart = textarea.selectionEnd = start + 1
      return
    }

    const navigationAction = this.getNavigationAction(event.key, event.shiftKey)
    if (!navigationAction) return

    if (!this.shouldNavigate(event, navigationAction)) return

    event.preventDefault()
    event.stopPropagation()
    this.closeDatePickers()

    if (navigationAction.shouldSubmit) {
      this.submitAndNavigate(event, navigationAction.direction)
    } else {
      this.navigateToCell(navigationAction.direction)
    }

    return false
  }

  getNavigationAction(key, shiftKey) {
    const actions = {
      'ArrowLeft': { direction: 'left', shouldSubmit: false },
      'ArrowRight': { direction: 'right', shouldSubmit: false },
      'ArrowUp': { direction: 'up', shouldSubmit: false },
      'ArrowDown': { direction: 'down', shouldSubmit: false },
      'Tab': {
        direction: shiftKey ? 'left' : 'right',
        shouldSubmit: true
      },
      'Enter': {
        direction: shiftKey ? 'up' : 'down',
        shouldSubmit: true
      }
    }

    return actions[key] || null
  }

  shouldNavigate(event, navigationAction) {
    const field = $(this.fieldTarget)
    const direction = navigationAction.direction
    const tagName = field.prop('tagName')?.toLowerCase()
    const shouldSubmit = navigationAction.shouldSubmit

    // For dropdowns (select), only navigate on Tab/Enter, not arrow keys
    // Arrow keys are used to change the dropdown selection
    if (tagName === 'select') {
      return shouldSubmit // Only navigate if it's Tab or Enter
    }

    // For Tab/Enter keys, always navigate (these are explicit navigation keys)
    if (shouldSubmit) {
      return true
    }

    // For textareas, don't navigate on arrow keys (let them move the cursor)
    if (tagName === 'textarea' && !shouldSubmit) {
      return false
    }

    // For text/number/date inputs on vertical navigation, always navigate
    if (direction === 'up' || direction === 'down') {
      return true
    }

    // For text/number/date inputs on horizontal navigation, check cursor position
    const input = event.target

    if (direction === 'left') {
      // Only navigate if cursor is at the start
      return input.selectionStart === 0
    }

    if (direction === 'right') {
      // Only navigate if cursor is at the end
      return input.selectionEnd === input.value.length
    }

    return true
  }

  closeDatePickers() {
    // Hide jQuery UI datepickers (legacy)
    $('.datepicker').hide()

    // Blur native date/datetime pickers
    const field = $(this.fieldTarget)
    if (field.attr('type') === 'date' || field.attr('type') === 'datetime-local') {
      field.blur()
    }
  }

  submitAndNavigate(event, direction) {
    const field = $(this.fieldTarget)
    const currentValue = field.val()
    const originalValue = field.data('originalValue')
    const currentTd = field.closest('td.inline-editable-form-container')

    // Don't submit immediately - let the blur event handle it
    // But mark that we need to submit so blur knows to actually submit
    if (currentValue !== originalValue) {
      currentTd.data('needsSubmit', true)
    }

    // Navigate immediately to prevent browser from changing focus
    this.navigateToCell(direction)

    // Nowhere to go (last column, last row, or a one-column table such as
    // Task Measurements): leave the cell anyway so the blur saves it. Without
    // this, Tab or Enter swallowed the key and the typed value sat unsaved
    // until the user clicked elsewhere.
    if (document.activeElement === this.fieldTarget) {
      this.fieldTarget.blur()
    }
  }

  navigateToCell(direction) {
    const currentTd = $(this.fieldTarget).closest('td.inline-editable-form-container')
    const field = $(this.fieldTarget)

    let targetTd = null

    switch (direction) {
      case 'left':
        targetTd = this.findPreviousEditableCell(currentTd)
        break
      case 'right':
        targetTd = this.findNextEditableCell(currentTd)
        break
      case 'up':
        targetTd = this.findCellAbove(currentTd)
        break
      case 'down':
        targetTd = this.findCellBelow(currentTd)
        break
    }

    if (targetTd && targetTd.length) {
      // Lazy account-grid cells carry no form until first edit — swap in the
      // editor now (synchronous, cloned from the column template) so the
      // focus below lands on a real field. No-op for eager cells.
      if (!materializeLazyCell(targetTd[0])) return

      // Show and focus the new cell FIRST - this is critical
      targetTd.find('.value').hide()
      targetTd.find('.form').show()
      targetTd.data('keyboardNav', true)

      const targetField = targetTd.find('[data-target~="inline-editable-form.field"]')
      if (targetField.length) {
        const targetValue = targetField.val()
        // See showForm() for why we don't overwrite originalValue.
        if (targetField.data('originalValue') === undefined) {
          targetField.data('originalValue', targetValue)
        }
        targetField.attr('value', targetValue)

        // FOCUS IMMEDIATELY - this is the key
        targetField[0].focus()

        const tagName = targetField.prop('tagName')?.toLowerCase()
        if (tagName !== 'select') {
          targetField[0].select()
        }
      }

      // NOW clean up the current cell.
      // Submit is handled by the blur event (via needsSubmit flag set in submitAndNavigate).
      // Do NOT submit here — focusing the target cell above triggers blur on the old field,
      // which already calls submitIfUpdated and submits if needsSubmit is set.
      //
      // The cell's .value element is NOT updated here — the server-rendered broadcast is
      // the only writer of cell content. The cell will keep showing the previous server
      // value (with a saving spinner) until the broadcast lands.
      currentTd.find('.value').show()
      currentTd.find('.form').hide()
    }
    // If no target found, do nothing (edge behavior: don't wrap)
  }

  findNextEditableCell(currentTd) {
    let nextTd = currentTd.next('td')

    // Keep looking until we find an editable cell or run out of cells
    while (nextTd.length) {
      if (this.isEditableCell(nextTd)) {
        return nextTd
      }
      nextTd = nextTd.next('td')
    }

    // No editable cell found to the right
    return null
  }

  findPreviousEditableCell(currentTd) {
    let prevTd = currentTd.prev('td')

    // Keep looking until we find an editable cell or run out of cells
    while (prevTd.length) {
      if (this.isEditableCell(prevTd)) {
        return prevTd
      }
      prevTd = prevTd.prev('td')
    }

    // No editable cell found to the left
    return null
  }

  findCellAbove(currentTd) {
    const currentTr = currentTd.closest('tr')
    const cellIndex = currentTd[0].cellIndex
    const prevTr = currentTr.prev('tr')

    if (!prevTr.length) {
      return null // No row above
    }

    const targetTd = $(prevTr[0].cells[cellIndex])

    // Check if the cell at this index is editable
    if (this.isEditableCell(targetTd)) {
      return targetTd
    }

    // If not editable, don't navigate
    return null
  }

  findCellBelow(currentTd) {
    const currentTr = currentTd.closest('tr')
    const cellIndex = currentTd[0].cellIndex
    const nextTr = currentTr.next('tr')

    if (!nextTr.length) {
      return null // No row below
    }

    const targetTd = $(nextTr[0].cells[cellIndex])

    // Check if the cell at this index is editable
    if (this.isEditableCell(targetTd)) {
      return targetTd
    }

    // If not editable, don't navigate
    return null
  }

  isEditableCell(td) {
    // Check if the td has the inline-editable-form-container class
    if (!td.hasClass('inline-editable-form-container')) {
      return false
    }

    // Lazy account-grid cells have no editor yet — navigateToCell
    // materializes one on entry.
    if (td.is('[data-controller~="lazy-cell"]')) {
      return true
    }

    // Check if there's an inline-editable-form controller
    const controller = td.find('[data-controller*="inline-editable-form"]')
    if (!controller.length) {
      return false
    }

    // Check if there's a field target (input/select)
    const field = td.find('[data-target~="inline-editable-form.field"]')
    return field.length > 0
  }

  focusCell(targetTd) {
    // Manually show the form (same as showForm method)
    targetTd.find('.value').hide()
    targetTd.find('.form').show()

    // Mark this cell as being accessed via keyboard navigation
    targetTd.data('keyboardNav', true)

    // Find the field and initialize it
    const field = targetTd.find('[data-target~="inline-editable-form.field"]')
    if (!field.length) return

    const currentValue = field.val()
    const inputType = field.attr('type')
    const tagName = field.prop('tagName')?.toLowerCase()

    // See showForm() for why we don't overwrite originalValue.
    if (field.data('originalValue') === undefined) {
      field.data('originalValue', currentValue)
    }
    field.attr('value', currentValue)

    // Focus immediately and synchronously to prevent browser from focusing elsewhere
    field[0].focus()

    // Use requestAnimationFrame only for select/click actions, not for initial focus
    if (tagName === 'select') {
      field.data('justShown', false)
      try {
        if (typeof field[0]?.click === 'function') {
          field[0].click()
        }
      } catch (e) {}
    } else {
      field[0].select()
    }
  }

  triggerEscapeAction() {
    const field = $(this.fieldTarget)
    field.val(field.data('originalValue'))
    this.markClean()
    field.blur()
    this.fieldTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }

  submitForm(event) {
    event.preventDefault()
    this.pendingSubmitValue = $(this.fieldTarget).val()
    this.markSaving()
    Rails.fire(this.formTarget, 'submit')
  }

  showForm(event) {
    const container = $(event.currentTarget).closest('.inline-editable-form-container')
    container.find('.value').hide()
    container.find('.form').show()

    this.currentContainer = container.get(0)

    const field = container.find('[data-target~="inline-editable-form.field"]')
    if (!field.length) return

    const currentValue = field.val()
    const inputType = field.attr('type')
    const tagName = field.prop('tagName')?.toLowerCase()


    const skipPicker = field.is('[data-skip-picker]')

    // originalValue is seeded once by setOriginalValue() on controller connect.
    // Don't clobber it here: if a typed value never reached the server (e.g.,
    // save dropped on tab close), the input still holds that stale typed value
    // on re-open. Overwriting originalValue with it would lock it in as "saved"
    // and every later blur would see currentValue === originalValue and skip
    // the submit — silently swallowing the user's input until they retype.
    if (field.data('originalValue') === undefined) {
      field.data('originalValue', currentValue)
    }
    field.attr('value', currentValue)
    field.focus()
    field.select()

    // Clear keyboard nav flag for mouse clicks
    container.data('keyboardNav', false)

    if (!skipPicker && !field.data('justShown')) {
      try {
        if (inputType === 'date' || inputType === 'datetime-local') {
          if (typeof field[0]?.showPicker === 'function') {
            field[0].showPicker()
          } else if (typeof field[0]?.click === 'function') {
            field[0].click()
          }
        } else if (tagName === 'select') {
          if (typeof field[0]?.click === 'function') {
            field[0].click()
          }
        }
      } catch (e) {}

      field.data('justShown', true)
    }

    this.startOutsideClickListener()
  }

  startOutsideClickListener() {
    // Idempotent: callable from showForm (mouse path), handleFieldFocus (any path),
    // or future callsites without leaking duplicate document listeners.
    if (this.handleOutsideClick) return

    this.handleOutsideClick = (event) => {
      const container = this.currentContainer
      const form = this.formTarget

      const isOutside = (!container || !container.contains(event.target))
        && (!form || !form.contains(event.target))

      if (isOutside) {
        this.hideForm($(container))
        document.removeEventListener('mousedown', this.handleOutsideClick)
        this.handleOutsideClick = null
      }
    }

    document.addEventListener('mousedown', this.handleOutsideClick)
  }

  truncateText(text, length = 25) {
    if (!text || text.length <= length) return text
    return text.substring(0, length) + '...'
  }

  isTextarea(container) {
    const controllerEl = container.find('[data-truncate-value="true"]')
    if (controllerEl.length) {
      return true
    }
    const field = container.find('[data-target~="inline-editable-form.field"]')
    return field.length && field.prop('tagName')?.toLowerCase() === 'textarea'
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  updateTippy(valueEl, fullText) {
    const el = valueEl[0]
    if (!el) return

    const scrollableContent = (text) =>
      `<div style="max-height: 200px; overflow-y: auto; white-space: pre-wrap;">${this.escapeHtml(text)}</div>`

    if (fullText && fullText.length > 25 && window.innerWidth > 1024) {
      if (el._tippy) {
        el._tippy.setContent(scrollableContent(fullText))
      } else {
        import('tippy.js').then(({ default: tippy }) => {
          el.dataset.title = fullText
          tippy(el, {
            content: scrollableContent(fullText),
            animation: 'scale-subtle',
            allowHTML: true,
            interactive: true,
            maxWidth: 400,
            theme: 'light-border',
            appendTo: () => document.body
          })
        })
      }
    } else if (el._tippy) {
      el._tippy.destroy()
      delete el.dataset.title
    }
  }

  hideForm(container) {
    // Hide the form and reveal the value display. The .value element is never
    // touched here — the server-rendered broadcast is the only writer of cell
    // content. If the user changed the value, submitForm has already added a
    // .saving spinner; the cell will keep showing the previous server value
    // until the broadcast replaces this <td> with the new server-rendered HTML.
    container.find('.form').hide()
    container.find('.value').show()
  }

  async fetchPartial(container, partial) {
    const response = await fetch(`/${partial}`)
    const html = await response.text()
    container.html(html)
  }
}
